class RouqeChatbox {
  constructor() {
    // Make chatbox instance globally accessible
    window.chatbox = this;
    
    // DOM Elements
    this.chatbox = document.getElementById('rouqe-chatbox');
    this.messagesContainer = document.getElementById('chatbox-messages');
    this.inputField = document.getElementById('chat-input');
    this.sendButton = document.getElementById('send-message');
    this.suggestedQuestionsContainer = document.getElementById('suggested-questions');

    // Store policies directly in the chatbox
    this.policies = {
      shipping: {
        methods: [
          { name: 'Canada Standard', time: '5-10 business days' },
          { name: 'US Standard', time: '5-10 business days' },
          { name: 'Australia and UK', time: '10-18 business days' },
          { name: 'Rest of World', time: '10-22 business days' }
        ],
        processing: '48 hours for in-stock items',
        notes: [
          'Please double-check your shipping address before ordering',
          'We\'re not responsible for customs fees',
          'Tracking provided for all orders'
        ]
      },
      returns: {
        timeframe: '14 days',
        conditions: [
          'Items must be in original condition',
          'Must be unworn',
          'All tags must be attached'
        ],
        process: [
          'Contact via email before shipping returns',
          'Use Return Portal',
          'Ship to provided return address'
        ],
        notes: [
          'Store credit may be offered in certain cases',
          'Return shipping costs are your responsibility',
          'Items must follow wash instructions to be eligible'
        ]
      }
    };

    // Initialize sizing state with stored measurements if they exist
    const storedMeasurements = localStorage.getItem('rouqe-measurements');
    this.sizingState = storedMeasurements ? 
        JSON.parse(storedMeasurements) : 
        {
            height: null,
            weight: null
        };

    // Define actual Shopify collections
    this.collections = {
      tops: {
        url: '/collections/polo',
        handle: 'polo'
      },
      bottoms: {
        url: '/collections/bottoms',
        handle: 'bottoms'
      },
      outerwear: {
        url: '/collections/outerwear',
        handle: 'outerwear'
      }
    };

    // Initialize product data
    this.productData = {};
    this.loadProductData();

    // Move size chart here instead of static property
    this.sizeChart = [
      { heightRange: ["5'3", "5'6"], weightRange: [110, 125], size: 'XS' },
      { heightRange: ["5'3", "5'6"], weightRange: [126, 140], size: 'Small' },
      { heightRange: ["5'3", "5'6"], weightRange: [141, 160], size: 'Medium' },
      { heightRange: ["5'3", "5'6"], weightRange: [161, 180], size: 'Large' },
      { heightRange: ["5'7", "5'10"], weightRange: [130, 145], size: 'Small' },
      { heightRange: ["5'7", "5'10"], weightRange: [146, 165], size: 'Medium' },
      { heightRange: ["5'7", "5'10"], weightRange: [166, 185], size: 'Large' },
      { heightRange: ["5'7", "5'10"], weightRange: [186, 210], size: 'XL' },
      { heightRange: ["5'11", "6'2"], weightRange: [150, 165], size: 'Medium' },
      { heightRange: ["5'11", "6'2"], weightRange: [166, 185], size: 'Large' },
      { heightRange: ["5'11", "6'2"], weightRange: [186, 205], size: 'XL' },
      { heightRange: ["5'11", "6'2"], weightRange: [206, 230], size: 'XXL' },
      { heightRange: ["6'3", "6'6"], weightRange: [170, 185], size: 'Large' },
      { heightRange: ["6'3", "6'6"], weightRange: [186, 210], size: 'XL' },
      { heightRange: ["6'3", "6'6"], weightRange: [211, 240], size: 'XXL' },
      { heightRange: ["6'3", "6'6"], weightRange: [241, 270], size: 'XXL' }
    ];

    // Add tracking state
    this.trackingState = {
        email: null,
        orderNumber: null
    };
    
    // Update tracking API endpoint
    this.trackingApiEndpoint = 'https://tracking-service-b2x2.onrender.com/track-order';

    // Initialize
    this.init();
  }

  init() {
    // Add event listeners
    this.sendButton.addEventListener('click', () => this.handleSendMessage());
    
    this.inputField.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Prevent default enter behavior
            this.handleSendMessage();
            // Blur the input to hide keyboard
            this.inputField.blur();
            // Scroll page back to top of chatbox
            this.chatbox.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    });

    // Display welcome message with golf animation
    this.addBotMessage(`
      Feel free to ask me anything you want!<br>
      I'll just be over here working on my short game...<br>
      <div class="golf-animation">
          <div class="golfer">🏌️</div>
          <div class="golf-ball">⚪</div>
          <div class="hole">⛳</div>
      </div>
  `.replace(/\s+/g, ' ').trim());
    
    // Set up suggested questions click handlers
    this.setupSuggestedQuestions();
  }

  setupSuggestedQuestions() {
    const buttons = document.querySelectorAll('.suggested-question-btn');
    buttons.forEach(button => {
      button.addEventListener('click', () => {
        const question = button.textContent;
        this.handleSuggestedQuestion(question);
      });
    });
  }

  handleSuggestedQuestion(question) {
    this.addUserMessage(question);
    
    switch(question) {
      case 'Return Policy':
        this.addBotMessage(this.generateReturnResponse(question));
        break;
        
      case 'Shipping Info':
        this.addBotMessage(this.generateShippingResponse(question));
        break;
        
      case 'Track My Order':
        this.handleTrackingRequest();
        break;
        
      case 'Restock Questions':
        this.addBotMessage(this.generateRestockResponse());
        break;
        
      case 'Help With Sizing':
        this.startSizingFlow();
        break;
        
      case 'Collaborations':
        this.handleCollaborationRequest();
        break;
    }
  }

  handleSendMessage() {
    const message = this.inputField.value.trim();
    if (message) {
      this.addUserMessage(message);
      this.processUserMessage(message);
      this.inputField.value = '';
    }
  }

  addUserMessage(message) {
    const messageDiv = document.createElement('div');
    messageDiv.className = 'message user-message';
    messageDiv.textContent = message;
    this.messagesContainer.appendChild(messageDiv);
    
    // Store the message div
    this.lastUserMessage = messageDiv;
  }

  addBotMessage(message) {
    const messageDiv = document.createElement('div');
    messageDiv.className = 'message bot-message';
    messageDiv.innerHTML = message.replace(/\n/g, '<br>');
    this.messagesContainer.appendChild(messageDiv);
    
    // Scroll within the messages container with smooth animation
    setTimeout(() => {
        // If we have a last user message, scroll to show it at the top
        if (this.lastUserMessage) {
            this.messagesContainer.scrollTo({
                top: this.lastUserMessage.offsetTop - this.messagesContainer.offsetTop,
                behavior: 'smooth'
            });
        }
        
        // If there's a measurement form or product grid, adjust scroll to show both user message and the interactive element
        const measurementForm = messageDiv.querySelector('.measurement-form');
        const productGrid = messageDiv.querySelector('.product-grid');
        if (measurementForm || productGrid) {
            setTimeout(() => {
                const element = measurementForm || productGrid;
                const scrollPosition = this.lastUserMessage ? 
                    this.lastUserMessage.offsetTop - this.messagesContainer.offsetTop :
                    element.offsetTop - this.messagesContainer.offsetTop;
                this.messagesContainer.scrollTo({
                    top: scrollPosition,
                    behavior: 'smooth'
                });
            }, 100);
        }
    }, 100);
  }

  scrollToBottom() {
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
  }

  generateShippingResponse(question) {
    let response = '✨ Thanks for your interest in our shipping info! Here\'s everything you need to know:\n\n';
    
    response += '<strong>⏳ Processing Time</strong>\n';
    response += '📦 Orders are processed within ' + this.policies.shipping.processing + '\n';
    response += '📩 You\'ll receive tracking details once your order ships!\n\n';
    
    response += '<strong>🌍 Estimated Delivery Times</strong>\n';
    this.policies.shipping.methods.forEach(method => {
        let emoji = '';
        switch(method.name) {
            case 'Canada Standard':
                emoji = '🇨🇦';
                break;
            case 'US Standard':
                emoji = '🇺🇸';
                break;
            case 'Australia and UK':
                emoji = '🇦🇺🇬🇧';
                break;
            case 'Rest of World':
                emoji = '🌎';
                break;
        }
        response += `${emoji} ${method.name}: ${method.time}\n`;
    });
    response += '\n';
    
    response += '<strong>📌 A Few Important Notes</strong>\n';
    this.policies.shipping.notes.forEach(note => {
        response += `${note}\n`;
    });
    response += '\n\n';
    
    response += '💡 Still have questions? We\'re happy to help! Email us at info@rouqesupport.com 📩';
    
    return response;
  }

  generateReturnResponse(question) {
    let response = '✨ Got a return? No worries! Here\'s what you need to know:\n\n';
    
    response += '<strong>🕒 Return Window</strong>\n';
    response += `You have ${this.policies.returns.timeframe} from the delivery date to send your item back.\n\n`;
    
    response += '<strong>📌 Return Requirements</strong>\n';
    this.policies.returns.conditions.forEach(condition => {
        response += `✅ ${condition}\n`;
    });
    response += '\n';
    
    response += '<strong>🚀 How to Start a Return</strong>\n';
    response += '1️⃣ Contact via email before shipping returns\n';
    response += '2️⃣ <a href="https://rouqegolf.com/apps/redo/returns-portal/login" target="_blank">Use Return Portal</a> [Click here to start your return]\n';
    response += '3️⃣ Ship to provided return address\n\n';
    
    response += '<strong>💡 A Few Quick Notes:</strong>\n';
    this.policies.returns.notes.forEach(note => {
        response += `${note}\n`;
    });
    response += '\n\n';
    
    response += '📩 Need help? Email us at info@rouqesupport.com – we\'re happy to assist!';
    
    return response;
  }

  generateRestockResponse() {
    let response = '✨ Thanks for your interest in our products!\n\n';
    
    response += 'While we don\'t usually restock items, you can be the first to know if we do! Here\'s how:\n\n';
    
    response += '1️⃣ Go to the product page of the item you want.\n';
    response += '2️⃣ Find the "Notify Me" widget.\n';
    response += '3️⃣ Enter your email and wait for the magic! ✨\n\n';
    
    response += '📩 If the item comes back, you\'ll be the first to know—no refreshing needed!\n\n';
    
    response += '💡 Pro Tip: Popular items do sometimes return, so it\'s always worth signing up!';
    
    return response;
  }

  async processUserMessage(message) {
    const lowerMessage = message.toLowerCase();
    
    // First check for predefined patterns
    if (this.handlePredefinedPatterns(lowerMessage)) {
        return;
    }

    // If no predefined pattern matches, use OpenAI
    try {
        this.addTypingIndicator();
        const response = await fetch('https://tracking-service-b2x2.onrender.com/api/chat', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ message: lowerMessage })
        });

        if (!response.ok) {
            throw new Error('Failed to get AI response');
        }

        const data = await response.json();
        this.removeTypingIndicator();
        this.addBotMessage(data.response);
    } catch (error) {
        console.error('Error getting AI response:', error);
        this.removeTypingIndicator();
        this.addBotMessage("I apologize, but I'm having trouble understanding. Could you please rephrase your question or use one of the suggested topics below?");
    }
  }

  handlePredefinedPatterns(lowerMessage) {
    // Return true if a predefined pattern was handled
    if (lowerMessage.includes('shipping') && !this.isTrackingQuestion(lowerMessage)) {
        this.addBotMessage(this.generateShippingResponse());
        return true;
    }
    if (lowerMessage.includes('return') || 
        lowerMessage.includes('refund') || 
        lowerMessage.includes('exchange')) {
        this.addBotMessage(this.generateReturnResponse());
        return true;
    }
    if (lowerMessage.includes('size') || lowerMessage.includes('sizing')) {
        this.startSizingFlow();
        return true;
    }
    if (this.isTrackingQuestion(lowerMessage)) {
        this.handleTrackingRequest();
        return true;
    }
    // Add comprehensive restock keyword checking
    if (this.isRestockQuestion(lowerMessage)) {
        this.addBotMessage(this.generateRestockResponse());
        return true;
    }
    return false;
  }

  addTypingIndicator() {
    const typingDiv = document.createElement('div');
    typingDiv.className = 'typing-indicator';
    typingDiv.innerHTML = '<span></span><span></span><span></span>';
    this.messagesContainer.appendChild(typingDiv);
    this.scrollToBottom();
  }

  removeTypingIndicator() {
    const typingIndicator = document.querySelector('.typing-indicator');
    if (typingIndicator) {
        typingIndicator.remove();
    }
  }

  startSizingFlow() {
    // Check if we have stored measurements
    if (this.sizingState.height && this.sizingState.weight) {
        const response = `👕 Welcome back! I see your measurements are ${this.sizingState.height} and ${this.sizingState.weight}lbs.\n\n` +
            'Would you like to:\n\n' +
            '<div class="sizing-options">' +
            '<button class="sizing-option" onclick="chatbox.showCategoryOptions()">Use Saved Measurements</button>' +
            '<button class="sizing-option" onclick="chatbox.resetAndStartNew()">Enter New Measurements</button>' +
            '</div>';
        
        this.addBotMessage(response);
    } else {
        // No stored measurements, start normal flow
        this.startNormalSizingFlow();
    }
  }

  startNormalSizingFlow() {
    const response = '👕 Let\'s find your perfect size!\n\n' +
      'What\'s your gender? This helps us with sizing calculations.\n\n' +
      '<div class="sizing-options">' +
      '<button class="sizing-option" onclick="chatbox.handleGenderSelection(\'male\')">👨 Male</button>' +
      '<button class="sizing-option" onclick="chatbox.handleGenderSelection(\'female\')">👩 Female</button>' +
      '</div>';
    
    this.addBotMessage(response);
  }

  resetAndStartNew() {
    // Add user message to maintain scroll context
    this.addUserMessage("Enter New Measurements");
    
    // Completely reset sizing state properties
    this.sizingState = {
        height: null,
        weight: null,
        gender: null,
        category: null
    };
    
    // Clear stored measurements from localStorage
    localStorage.removeItem('rouqe-measurements');
    
    // Clear any existing event listeners by removing old elements
    const oldForm = document.querySelector('.measurement-form');
    if (oldForm) {
        oldForm.remove();
    }
    
    // Start fresh sizing flow without clearing chat
    this.startNormalSizingFlow();
  }

  handleGenderSelection(gender) {
    this.sizingState.gender = gender;
    this.addUserMessage(gender.charAt(0).toUpperCase() + gender.slice(1));
    this.askForMeasurements();
  }

  askForMeasurements() {
    const response = '📏 Let\'s get your measurements!\n\n' +
        '<div class="measurement-form">' +
            '<div class="height-selector">' +
                '<label>Height: <span id="height-display">5\'10"</span></label>' +
                '<div class="slider-container">' +
                    '<input type="range" id="height-slider" min="60" max="84" value="70" class="measurement-slider">' +
                    '<div class="slider-labels">' +
                        '<span>5\'0"</span>' +
                        '<span>7\'0"</span>' +
                    '</div>' +
                '</div>' +
            '</div>' +
            '<div class="weight-selector">' +
                '<label>Weight: <span id="weight-display">160 lbs</span></label>' +
                '<div class="slider-container">' +
                    '<input type="range" id="weight-slider" min="100" max="300" value="160" step="5" class="measurement-slider">' +
                    '<div class="slider-labels">' +
                        '<span>100 lbs</span>' +
                        '<span>300 lbs</span>' +
                    '</div>' +
                '</div>' +
            '</div>' +
            '<button id="measurements-submit" class="measurement-submit">Continue</button>' +
        '</div>';

    this.addBotMessage(response);
    
    // Add event listeners after the elements are added to DOM
    setTimeout(() => {
        const heightSlider = document.getElementById('height-slider');
        const weightSlider = document.getElementById('weight-slider');
        const heightDisplay = document.getElementById('height-display');
        const weightDisplay = document.getElementById('weight-display');
        const submitButton = document.getElementById('measurements-submit');

        // Convert height in inches to feet and inches string
        const formatHeight = (inches) => {
            const feet = Math.floor(inches / 12);
            const remainingInches = inches % 12;
            return `${feet}'${remainingInches}"`;
        };

        // Store reference to this for use in event listeners
        const self = this;

        // Update displays when sliders move
        heightSlider.addEventListener('input', function(e) {
            const height = formatHeight(this.value);
            heightDisplay.textContent = height;
            // Update sizing state immediately
            self.sizingState.height = height;
            console.log('Updated height:', height); // Debug log
        });

        weightSlider.addEventListener('input', function(e) {
            const weight = parseInt(this.value);
            weightDisplay.textContent = `${weight} lbs`;
            // Update sizing state immediately
            self.sizingState.weight = weight;
            console.log('Updated weight:', weight); // Debug log
        });

        // Handle submission
        submitButton.addEventListener('click', () => {
            console.log('Current sizing state:', self.sizingState);
            // Save to localStorage
            localStorage.setItem('rouqe-measurements', JSON.stringify(self.sizingState));
            
            // Add user message to maintain scroll context
            self.addUserMessage(`${self.sizingState.height}, ${self.sizingState.weight}lbs`);
            
            // Show success message and continue
            self.addBotMessage(`✅ Great! I've got your measurements: ${self.sizingState.height}, ${self.sizingState.weight}lbs`);
            self.showCategoryOptions();
        });

        // Set initial values and update state
        const initialHeight = formatHeight(heightSlider.value);
        const initialWeight = parseInt(weightSlider.value);
        heightDisplay.textContent = initialHeight;
        weightDisplay.textContent = `${initialWeight} lbs`;
        self.sizingState.height = initialHeight;
        self.sizingState.weight = initialWeight;
        console.log('Initial sizing state:', self.sizingState); // Debug log
    }, 100);
  }

  parseHeight(input) {
    input = input.toLowerCase().trim();
    console.log('Height input to parse:', input);
    
    // First normalize any fancy quotes to standard ones
    input = input
        .replace(/[\u2018\u2019\u201B]/g, "'")  // Normalize smart single quotes
        .replace(/[\u201C\u201D]/g, '"');       // Normalize smart double quotes
    
    // Handle various formats
    const formats = [
        /(\d+)'(\d+)\"?/,      // 5'10" or 5'10
        /(\d+)'(\d+)/,         // 5'10
        /(\d+)\s*'\s*(\d+)/,   // 5 ' 10
        /(\d+)\s+(\d+)/        // 5 10
    ];
    
    for (let pattern of formats) {
        const match = input.match(pattern);
        if (match) {
            const feet = parseInt(match[1]);
            const inches = parseInt(match[2]);
            
            // Validate reasonable height ranges
            if (feet >= 4 && feet <= 7 && inches >= 0 && inches <= 11) {
                const cm = Math.round((feet * 12 + inches) * 2.54);
                console.log('Parsed height successfully:', {
                    feet,
                    inches,
                    cm,
                    originalInput: input,
                    normalizedInput: input
                });
                return cm;
            }
        }
    }
    
    console.log('Input character codes:', [...input].map(c => `${c}: ${c.charCodeAt(0)}`));
    console.log('No valid height pattern matched');
    return null;
  }

  parseWeight(input) {
    input = input.toLowerCase().trim();
    console.log('Weight input to parse:', input);
    
    // Handle kg input
    if (input.includes('kg') || input.includes('kilo')) {
      const kg = parseInt(input);
      if (kg >= 45 && kg <= 120) return kg;
    }
    
    // Handle lbs input
    const lbsPatterns = [
      /^(\d+)(?:\s*(?:lbs?|pounds?))?$/, // 160 or 160lbs or 160 lbs
      /^(\d+)\s*$/, // just the number
      /^(\d+)\s+(?:lbs?|pounds?)$/ // 160 lbs with space
    ];

    for (let pattern of lbsPatterns) {
      const match = input.match(pattern);
      if (match) {
        console.log('Weight pattern matched:', pattern, 'Match:', match);
        const lbs = parseInt(match[1]);
        if (lbs >= 100 && lbs <= 265) {
          return Math.round(lbs * 0.453592);
        }
      }
    }
    
    console.log('No weight pattern matched');
    return null;
  }

  showCategoryOptions() {
    // Add user message to maintain scroll context
    this.addUserMessage("Check Another Item");
    
    const response = '🛍️ What type of item are you looking for?\n\n' +
      '<div class="sizing-options">' +
      '<button class="sizing-option" onclick="chatbox.handleCategorySelection(\'tops\')">👕 Tops</button>' +
      '<button class="sizing-option" onclick="chatbox.handleCategorySelection(\'bottoms\')">👖 Bottoms</button>' +
      '<button class="sizing-option" onclick="chatbox.handleCategorySelection(\'outerwear\')">🧥 Outerwear</button>' +
      '</div>';
    
    this.addBotMessage(response);
    this.inputField.removeAttribute('data-expect');
    this.inputField.placeholder = 'Type your message...';
  }

  handleCategorySelection(category) {
    this.sizingState.category = category;
    this.addUserMessage(category.charAt(0).toUpperCase() + category.slice(1));
    this.showCollectionOptions(category);
  }

  async showCollectionOptions(category) {
    // Check if we need to load the data
    if (!this.productData[category]) {
      await this.loadProductData();
    }

    const products = this.productData[category] || [];
    
    if (products.length === 0) {
      this.addBotMessage(`I'm having trouble loading the products. Please try again or email us at info@rouqesupport.com for assistance.`);
      return;
    }

    let buttons = products.map(product => `
      <button class="sizing-option product-option" onclick="chatbox.handleProductSelection('${category}', '${product.handle}')">
        <img src="${product.image}" alt="${product.title}" class="product-thumbnail">
        <span>${product.title}</span>
      </button>
    `).join('');

    const response = `📦 Which item from our ${category} collection are you interested in?\n\n` +
      '<div class="sizing-options product-grid">' + buttons + '</div>';
    
    this.addBotMessage(response);
  }

  handleProductSelection(category, productHandle) {
    const product = this.productData[category].find(p => p.handle === productHandle);
    if (!product) {
      console.error('Product not found:', productHandle);
      return;
    }

    // Add the user's selection as a message
    this.addUserMessage(product.title);

    // Get measurements from localStorage if they're not in memory
    if (!this.sizingState.height || !this.sizingState.weight) {
      const storedMeasurements = localStorage.getItem('rouqe-measurements');
      if (storedMeasurements) {
        const stored = JSON.parse(storedMeasurements);
        this.sizingState.height = stored.height;
        this.sizingState.weight = stored.weight;
      } else {
        console.error('No measurements found');
        this.startSizingFlow();
        return;
      }
    }

    const recommendedSize = this.calculateRecommendedSize(
        this.sizingState.height,
        this.sizingState.weight,
        category
    );
    
    let response = `🎯 Size <strong>${recommendedSize}</strong> is the perfect option for you!\n\n`;
response += `This recommendation is based on your measurements (<strong>${this.sizingState.height} and ${this.sizingState.weight}lbs</strong>). ✅\n\n`;
response += `💡 <strong>Fit Tip:</strong> Prefer a looser fit? Consider sizing up for extra comfort.\n\n`;
response += `🔄 <strong>Want to check another item?</strong> Let us know!\n\n`;
response += '<div class="sizing-options">' +
  '<button class="sizing-option" onclick="chatbox.showCategoryOptions()">Check Another Item</button>' +
  '<button class="sizing-option" onclick="chatbox.resetAndStartNew()">Enter New Measurements</button>' +
  '</div>';

    this.addBotMessage(response);
    this.resetSizingState();
  }

  calculateRecommendedSize(height, weight, category) {
      console.log('Calculating size for:', { height, weight, category });
      
      // Helper function to convert height to inches
      const heightToInches = (heightStr) => {
          const parts = heightStr.split("'");
          const feet = parseInt(parts[0]);
          const inches = parts[1] ? parseInt(parts[1]) : 0;
          return (feet * 12) + inches;
      };

      // Convert customer height to inches
      const customerHeightInches = heightToInches(height);
      console.log('Customer height in inches:', customerHeightInches);

      // Find the matching size range
      let recommendedSize = null;
      for (const range of this.sizeChart) {
          // Convert range heights to inches
          const minHeight = heightToInches(range.heightRange[0]);
          const maxHeight = heightToInches(range.heightRange[1]);
          
          console.log('Checking range:', {
              min: range.heightRange[0],
              max: range.heightRange[1],
              minInches: minHeight,
              maxInches: maxHeight,
              weight: range.weightRange,
              size: range.size
          });

          // Check if height and weight are in range
          if (customerHeightInches >= minHeight && 
              customerHeightInches <= maxHeight && 
              weight >= range.weightRange[0] && 
              weight <= range.weightRange[1]) {
              recommendedSize = range.size;
              console.log('Found matching size:', recommendedSize);
              break;
          }
      }

      // Size up for outerwear
      if (category === 'outerwear' && recommendedSize) {
          const sizes = ['XS', 'Small', 'Medium', 'Large', 'XL', 'XXL'];
          const currentIndex = sizes.indexOf(recommendedSize);
          if (currentIndex < sizes.length - 1) {
              recommendedSize = sizes[currentIndex + 1];
              console.log('Sized up for outerwear:', recommendedSize);
          }
      }

      console.log('Final recommended size:', recommendedSize);
      return recommendedSize || 'Medium';
  }

  resetSizingState() {
      // Only reset the category, keep the measurements
      this.sizingState = {
          ...this.sizingState,
          category: null
      };
  }

  async loadProductData() {
    for (const category in this.collections) {
      const collection = this.collections[category];
      try {
        // Log the fetch attempt
        console.log(`Fetching products for ${category}...`);
        
        const response = await fetch(`${collection.url}/products.json`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        
        // Log the received data
        console.log(`Received data for ${category}:`, data);

        this.productData[category] = data.products.map(product => ({
          id: product.id,
          title: product.title,
          handle: product.handle,
          image: product.images[0]?.src || '',
          available: !product.variants[0]?.available,
          description: product.body_html || ''
        }));

        // Log the processed products
        console.log(`Processed products for ${category}:`, this.productData[category]);
      } catch (error) {
        console.error(`Error loading ${category} products:`, error);
      }
    }
  }

  handleSizingInput(input) {
      console.log('Raw sizing input:', input);
      
      // Simple split of "6'3 190lbs" into height and weight
      const parts = input.split(' ');
      if (parts.length !== 2) {
          console.error('Invalid input format');
          return false;
      }

      // Store raw height (e.g., "6'3") and weight (remove 'lbs' and parse as number)
      this.sizingState.height = parts[0];
      this.sizingState.weight = parseInt(parts[1]);
      
      console.log('Stored measurements:', {
          height: this.sizingState.height,
          weight: this.sizingState.weight
      });

      return true;
  }

  // Add this new method to handle tracking requests
  handleTrackingRequest() {
      const trackingForm = `
          <div class="tracking-form">
              <div class="input-group">
                  <input type="email" id="tracking-email" placeholder="Email used for order" class="tracking-input">
              </div>
              <div class="input-group">
                  <input type="text" id="tracking-order" placeholder="Order number (#1234)" class="tracking-input">
              </div>
              <button id="track-order-btn" class="tracking-submit">Track My Package 📦</button>
          </div>
      `;

      this.addBotMessage(`
          📦 Let's find your order! 
          
          Please enter your order details below, and I'll check its status right away.
          ${trackingForm}
      `);

      // Add event listeners after the form is added to DOM
      setTimeout(() => {
          const trackButton = document.getElementById('track-order-btn');
          const emailInput = document.getElementById('tracking-email');
          const orderInput = document.getElementById('tracking-order');

          // Add input event listeners for real-time validation
          emailInput.addEventListener('input', () => {
              this.validateTrackingForm(emailInput, orderInput, trackButton);
          });

          orderInput.addEventListener('input', () => {
              this.validateTrackingForm(emailInput, orderInput, trackButton);
          });

          trackButton.addEventListener('click', () => {
              if (emailInput.value && orderInput.value) {
                  this.submitTracking(emailInput.value, orderInput.value);
              }
          });
      }, 100);
  }

  validateTrackingForm(emailInput, orderInput, trackButton) {
      const isValidEmail = emailInput.value.includes('@');
      const isValidOrder = orderInput.value.trim().length > 0;
      
      // Enable/disable button based on validation
      trackButton.disabled = !(isValidEmail && isValidOrder);
      trackButton.style.opacity = (isValidEmail && isValidOrder) ? '1' : '0.5';
  }

  async submitTracking(email, orderNumber) {
      if (!email || !orderNumber) {
          this.addBotMessage('❌ Please provide both email and order number.');
          return;
      }

      this.addUserMessage(`Track Order: ${orderNumber}`);
      
      try {
          console.log('Submitting tracking request to:', this.trackingApiEndpoint);
          console.log('Request data:', { email, orderNumber });
          
          const response = await fetch(this.trackingApiEndpoint, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
              },
              credentials: 'include',
              body: JSON.stringify({ email, orderNumber })
          });

          console.log('Response status:', response.status);
          
          if (!response.ok) {
              const errorText = await response.text();
              console.error('Error response:', errorText);
              throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          console.log('Tracking data received:', data);
          this.displayTrackingInfo(data);
      } catch (error) {
          console.error('Tracking error:', error);
          this.addBotMessage('🔍 We couldn\'t find an order matching the email and order number provided. Please double-check your email and the order number from your confirmation email and try again.');
      }
  }

  displayTrackingInfo(trackingData) {
      let progressWidth;
      let activeStep;
      let statusText;
      let statusEmoji;
      
      switch(trackingData.status) {
          case 'processing':
              progressWidth = '0%';
              activeStep = 0;
              statusText = 'Processing';
              statusEmoji = '⚙️';
              break;
          case 'shipped':
              progressWidth = '50%';
              activeStep = 1;
              statusText = 'In Transit';
              statusEmoji = '🚚';
              break;
          case 'delivered':
              progressWidth = '100%';
              activeStep = 2;
              statusText = 'Delivered';
              statusEmoji = '✅';
              break;
      }

      const progressBar = `
          <div class="tracking-progress">
              <div class="progress-steps">
                  <div class="progress-bar"><div class="progress-fill"></div></div>
                  <div class="progress-step ${activeStep >= 0 ? 'active' : ''} ${activeStep > 0 ? 'completed' : ''}">
                      <div class="progress-step-icon">⚙️</div>
                      <div class="progress-step-label">Processing</div>
                  </div>
                  <div class="progress-step ${activeStep >= 1 ? 'active' : ''} ${activeStep > 1 ? 'completed' : ''}">
                      <div class="progress-step-icon">🚚</div>
                      <div class="progress-step-label">In Transit</div>
                  </div>
                  <div class="progress-step ${activeStep >= 2 ? 'active' : ''}">
                      <div class="progress-step-icon">✅</div>
                      <div class="progress-step-label">Delivered</div>
                  </div>
              </div>
          </div>
      `.replace(/\s+/g, ' ').trim();

      let message = '📦 Here\'s your order status:\n\n';
      message += `Your order is <strong>${statusText}</strong> ${statusEmoji}\n\n`;
      message += progressBar;
      
      // Add the tracking details below the progress bar
      switch(trackingData.status) {
          case 'processing':
              message += `\nExpected ship date: ${trackingData.expectedShipDate}\n`;
              break;
          case 'shipped':
              message += `\nCarrier: ${trackingData.carrier}\n`;
              message += `Tracking Number: ${trackingData.trackingNumber}\n`;
              message += `\nFor a complete tracking history or any questions about your order status, please contact the courier directly and provide your tracking number.\n`;
              if (trackingData.trackingUrl) {
                  message += `\n<a href="${trackingData.trackingUrl}" target="_blank" class="tracking-link">Click here to track on carrier's website</a>`;
              }
              break;
          case 'delivered':
              message += `\nDelivered on: ${trackingData.deliveryDate}\n`;
              message += `Carrier: ${trackingData.deliveryLocation}\n`;
              if (trackingData.trackingNumber) {
                  message += `Tracking Number: ${trackingData.trackingNumber}\n`;
                  message += `\nFor a complete tracking history or any questions about your order status, please contact the courier directly and provide your tracking number.\n`;
              }
              if (trackingData.trackingUrl) {
                  message += `\n<a href="${trackingData.trackingUrl}" target="_blank" class="tracking-link">View delivery details</a>`;
              }
              break;
      }

      this.addBotMessage(message);

      // Delay the animation
      setTimeout(() => {
          const progressFill = document.querySelector('.progress-fill');
          if (progressFill) {
              progressFill.style.width = progressWidth;
          }

          // Trigger icon animations
          const steps = document.querySelectorAll('.progress-step-icon');
          steps.forEach((icon, index) => {
              if (index <= activeStep) {
                  setTimeout(() => {
                      icon.classList.add('grow', 'active');
                  }, (index + 1) * 1000); // Adjust timing to match progress bar
              }
          });
      }, 500); // Initial delay before starting the animation
  }

  handleCollaborationRequest() {
    const collaborationForm = `
        <div class="collaboration-form">
            <div class="input-group">
                <label class="collab-label">👤 Your Name</label>
                <input type="text" id="collab-name" placeholder="Enter your full name" class="collab-input" required>
            </div>
            <div class="input-group">
                <label class="collab-label">📧 Email Address</label>
                <input type="email" id="collab-email" placeholder="Where we can reach you" class="collab-input" required>
            </div>
            <div class="input-group">
                <label class="collab-label">📸 Instagram</label>
                <input type="text" id="collab-instagram" placeholder="@yourusername (optional)" class="collab-input">
            </div>
            <div class="input-group">
                <label class="collab-label">🎵 TikTok</label>
                <input type="text" id="collab-tiktok" placeholder="@yourusername (optional)" class="collab-input">
            </div>
            <div class="input-group">
                <label class="collab-label">🐦 X (Twitter)</label>
                <input type="text" id="collab-twitter" placeholder="@yourusername (optional)" class="collab-input">
            </div>
            <div class="input-group">
                <label class="collab-label">💭 Tell Us About Yourself</label>
                <textarea id="collab-reason" placeholder="Share your story and why you'd like to collaborate with us!" class="collab-input" required></textarea>
            </div>
            <button id="submit-collab" class="collab-submit">Submit Request ✨</button>
        </div>
    `;

    this.addBotMessage(`
        ✨ We're excited to hear from you! 
        Let's create something amazing together. Fill out the form below and let's get started!
        ${collaborationForm}
    `);

    // Add event listener after form is added to DOM
    setTimeout(() => {
        const submitButton = document.getElementById('submit-collab');
        submitButton.addEventListener('click', () => this.submitCollaborationForm());
    }, 100);
  }

  async submitCollaborationForm() {
    const name = document.getElementById('collab-name').value.trim();
    const email = document.getElementById('collab-email').value.trim();
    const instagram = document.getElementById('collab-instagram').value.trim();
    const tiktok = document.getElementById('collab-tiktok').value.trim();
    const twitter = document.getElementById('collab-twitter').value.trim();
    const reason = document.getElementById('collab-reason').value.trim();

    if (!name || !email || !reason) {
        this.addBotMessage('Please fill out all required fields (Name, Email, and Reason for collaboration).');
        return;
    }

    try {
        const response = await fetch('https://tracking-service-b2x2.onrender.com/api/collaborate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                name,
                email,
                instagram,
                tiktok,
                twitter,
                reason
            })
        });

        if (!response.ok) throw new Error('Submission failed');

        // Add user message to maintain scroll context
        this.addUserMessage("Collaboration Request Submitted");

        const confirmationMessage = `
            🎉 Fantastic! Your collaboration request has been successfully submitted!
            
            ✨ We're thrilled about the possibility of working together and can't wait to review your application.
            
            📧 Keep an eye on your inbox - our team will reach out within 2-3 business days with next steps.
            
            💫 Thank you for choosing to be part of the Rouqe community!
        `;

        this.addBotMessage(confirmationMessage);

        // Scroll to the confirmation message
        setTimeout(() => {
            if (this.lastUserMessage) {
                this.messagesContainer.scrollTo({
                    top: this.lastUserMessage.offsetTop - this.messagesContainer.offsetTop,
                    behavior: 'smooth'
                });
            }
        }, 100);

    } catch (error) {
        console.error('Collaboration submission error:', error);
        this.addBotMessage('🙈 Oops! Something went wrong with submitting your request. Please try again later or reach out to us directly at collaborations@rouqegolf.com - we\'d love to hear from you!');
    }
  }

  isTrackingQuestion(message) {
    const trackingKeywords = [
        'track',
        'tracking',
        'where is',
        'where\'s',
        'status',
        'package',
        'order status',
        'delivery status',
        'lost',
        'missing',
        'cant find',
        'can\'t find',
        'never arrived',
        'not here',
        'delayed',
        'late',
        'order is',
        'my order',
        'find my order',
        'locate',
        'delivery'
    ];
    
    return trackingKeywords.some(keyword => 
        message.toLowerCase().includes(keyword.toLowerCase())
    );
  }

  // Add this new method to check for restock-related questions
  isRestockQuestion(message) {
    const restockKeywords = [
        'restock',
        'back in stock',
        'when will',
        'coming back',
        'sold out',
        'available',
        'in stock',
        'extra',
        'more',
        'laying around',
        'left',
        'still have',
        'get more',
        'notify',
        'alert',
        'email me',
        'let me know'
    ];
    
    return restockKeywords.some(keyword => 
        message.toLowerCase().includes(keyword.toLowerCase())
    );
  }
}

// Initialize chatbox when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
new RouqeChatbox();
}); 